import type { ReactNode } from "react";
import { useMemo } from "react";
import { useSearchParams } from "react-router";
import { CheckCircle2, XCircle } from "lucide-react";
import { FormattedMessage, useIntl } from "react-intl";

import { CenteredLoader, InlineNotice, PageIntro } from "@/components/common/index";
import { IntegrationChannelIcon } from "@/components/integrations/integration-channel-icon";
import { InstagramConnectionCard } from "@/components/integrations/instagram-connection-card";
import { useRouteBrand } from "@/hooks/use-route-brand";
import { getInstagramCallbackErrorMessage } from "@/lib/instagram-callback-errors";
import { COMING_SOON_INTEGRATION_CHANNELS, REQUEST_INTEGRATION_CHANNEL } from "@/lib/integrations";
import { Badge, Button, Card, CardContent, CardDescription, CardTitle, cn } from "@runcheetah/ui";

type CallbackStatus = "success" | "error";

const isCallbackStatus = (value: string | null): value is CallbackStatus =>
  value === "success" || value === "error";

function ComingSoonIntegrationCard({
  id,
  accentClass,
  description,
  name,
}: (typeof COMING_SOON_INTEGRATION_CHANNELS)[number]) {
  const intl = useIntl();

  return (
    <Card className="flex min-h-48 flex-col rounded-2xl border-border/70 bg-card py-0 shadow-none">
      <CardContent className="flex flex-1 flex-col gap-5 p-4 sm:p-5">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border shadow-sm",
              accentClass,
            )}
          >
            <IntegrationChannelIcon channelId={id} className="h-5 w-5" />
          </div>
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <CardTitle className="text-base leading-5">{intl.formatMessage(name)}</CardTitle>
              <Badge
                shape="pill"
                variant="outline"
                className="border-transparent bg-muted px-2 py-0.5 text-[10px] font-bold tracking-wide text-muted-foreground uppercase"
              >
                <FormattedMessage id="integrations.comingSoonBadge" defaultMessage="Coming soon" />
              </Badge>
            </div>
            <CardDescription className="text-sm leading-5">
              {intl.formatMessage(description)}
            </CardDescription>
          </div>
        </div>
        <Button variant="outline" disabled className="mt-auto w-full bg-muted/40 font-semibold">
          <FormattedMessage id="integrations.notifyMe" defaultMessage="Notify me" />
        </Button>
      </CardContent>
    </Card>
  );
}

function RequestIntegrationStrip() {
  const intl = useIntl();

  return (
    <section className="rounded-2xl border border-dashed border-border/70 bg-card px-4 py-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div
            className={cn(
              "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border shadow-sm",
              REQUEST_INTEGRATION_CHANNEL.accentClass,
            )}
          >
            <IntegrationChannelIcon channelId="request" className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-bold tracking-tight">
              {intl.formatMessage(REQUEST_INTEGRATION_CHANNEL.name)}
            </h2>
            <p className="mt-1 text-sm leading-5 text-muted-foreground">
              {intl.formatMessage(REQUEST_INTEGRATION_CHANNEL.description)}
            </p>
          </div>
        </div>
        <Button className="shrink-0 font-semibold sm:min-w-36">
          {intl.formatMessage(REQUEST_INTEGRATION_CHANNEL.actionLabel)}
        </Button>
      </div>
    </section>
  );
}

export function IntegrationsPageContent({
  callback,
  callbackErrorMessage,
  instagramCard,
}: {
  callback: CallbackStatus | null;
  callbackErrorMessage: string;
  instagramCard: ReactNode;
}) {
  return (
    <div className="mx-auto w-full max-w-5xl space-y-7 px-5 py-8 sm:px-6 lg:py-10">
      <PageIntro
        title={<FormattedMessage id="integrations.title" defaultMessage="Integrations" />}
      />

      {callback === "success" ? (
        <InlineNotice tone="success" icon={<CheckCircle2 className="h-4 w-4" />}>
          <p>
            <FormattedMessage
              id="integrations.instagramConnectedSuccess"
              defaultMessage="Instagram connected successfully."
            />
          </p>
        </InlineNotice>
      ) : null}

      {callback === "error" ? (
        <InlineNotice tone="danger" icon={<XCircle className="h-4 w-4" />}>
          <p>{callbackErrorMessage}</p>
        </InlineNotice>
      ) : null}

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
        <div className="md:col-span-2 xl:col-span-2">{instagramCard}</div>
        {COMING_SOON_INTEGRATION_CHANNELS.map((integration) => (
          <ComingSoonIntegrationCard key={integration.id} {...integration} />
        ))}
      </section>

      <RequestIntegrationStrip />
    </div>
  );
}

export default function IntegrationsPage() {
  const intl = useIntl();
  const [searchParams] = useSearchParams();
  const { brand, brandQuery } = useRouteBrand();
  const instagramParam = searchParams.get("instagram");
  const errorReason = searchParams.get("reason");
  const callback: CallbackStatus | null = useMemo(
    () => (isCallbackStatus(instagramParam) ? instagramParam : null),
    [instagramParam],
  );
  const callbackErrorMessage = getInstagramCallbackErrorMessage(errorReason, intl);

  return (
    <IntegrationsPageContent
      callback={callback}
      callbackErrorMessage={callbackErrorMessage}
      instagramCard={
        brand ? (
          <InstagramConnectionCard brandId={brand.id} />
        ) : brandQuery.isPending ? (
          <Card className="min-h-48 rounded-2xl border-border/70 bg-card py-6 shadow-none">
            <CardContent className="px-6">
              <CenteredLoader
                label={intl.formatMessage({
                  id: "integrations.brandLoading",
                  defaultMessage: "Loading brand…",
                })}
                className="min-h-32 justify-start"
              />
            </CardContent>
          </Card>
        ) : (
          <InlineNotice>
            <FormattedMessage
              id="integrations.createBrandNotice"
              defaultMessage="Create a brand before connecting Instagram."
            />
          </InlineNotice>
        )
      }
    />
  );
}
